import type { PostHog } from 'posthog-js';

const POSTHOG_INITIALIZATION_DELAY_MS = 10_000;
const POSTHOG_INTERACTION_IDLE_TIMEOUT_MS = 1_500;
const MAX_PENDING_OPERATIONS = 200;

type PostHogOperation = (client: PostHog) => void;

let loadedClient: PostHog | null = null;
let loadingPromise: Promise<PostHog | null> | null = null;
let initializationTimer: number | null = null;
let interactionAnimationFrame: number | null = null;
let interactionIdleCallback: number | null = null;
let interactionLoadTimer: number | null = null;
let interactionListenersRegistered = false;
let missingApiKeyLogged = false;

const pendingOperations: PostHogOperation[] = [];

function removeInteractionListeners() {
  if (!interactionListenersRegistered || typeof window === 'undefined') {
    return;
  }

  window.removeEventListener('pointerdown', handleFirstInteraction);
  window.removeEventListener('keydown', handleFirstInteraction);
  interactionListenersRegistered = false;
}

function clearDeferredInitialization() {
  if (typeof window !== 'undefined') {
    if (initializationTimer !== null) {
      window.clearTimeout(initializationTimer);
      initializationTimer = null;
    }

    if (interactionAnimationFrame !== null) {
      window.cancelAnimationFrame(interactionAnimationFrame);
      interactionAnimationFrame = null;
    }

    if (interactionIdleCallback !== null) {
      window.cancelIdleCallback(interactionIdleCallback);
      interactionIdleCallback = null;
    }

    if (interactionLoadTimer !== null) {
      window.clearTimeout(interactionLoadTimer);
      interactionLoadTimer = null;
    }
  }

  removeInteractionListeners();
}

function flushPendingOperations(client: PostHog) {
  const operations = pendingOperations.splice(0);

  for (const operation of operations) {
    try {
      operation(client);
    } catch (error) {
      console.error('[PostHog] Failed to run a queued operation:', error);
    }
  }
}

export function initializePostHogClient(client: PostHog): PostHog | null {
  if (typeof window === 'undefined') return null;
  if (loadedClient) return loadedClient;

  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;

  if (!apiKey) {
    if (!missingApiKeyLogged) {
      console.error(
        '[PostHog Init] NEXT_PUBLIC_POSTHOG_KEY is not configured!'
      );
      missingApiKeyLogged = true;
    }

    pendingOperations.splice(0);
    clearDeferredInitialization();

    return null;
  }

  if (!client.__loaded) {
    client.init(apiKey, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://pht.upzi.vn',
      ui_host: 'https://us.posthog.com',
      defaults: '2025-05-24',
      capture_exceptions: true,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_dead_clicks: false,
      disable_surveys: true,
      debug: process.env.NODE_ENV === 'development',
    });
  }

  loadedClient = client;
  clearDeferredInitialization();
  flushPendingOperations(client);

  return client;
}

export function loadPostHog(): Promise<PostHog | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (loadedClient) return Promise.resolve(loadedClient);
  if (loadingPromise) return loadingPromise;

  clearDeferredInitialization();
  loadingPromise = import('posthog-js')
    .then(({ default: client }) => initializePostHogClient(client))
    .catch(error => {
      pendingOperations.splice(0);
      console.error('[PostHog Init] Failed to load PostHog:', error);

      return null;
    });

  return loadingPromise;
}

function schedulePostHogAfterInteraction() {
  if (
    typeof window === 'undefined' ||
    interactionAnimationFrame !== null ||
    interactionIdleCallback !== null ||
    interactionLoadTimer !== null
  ) {
    return;
  }

  removeInteractionListeners();
  interactionAnimationFrame = window.requestAnimationFrame(() => {
    interactionAnimationFrame = null;

    const startLoading = () => {
      interactionIdleCallback = null;
      interactionLoadTimer = null;
      void loadPostHog();
    };

    if (typeof window.requestIdleCallback === 'function') {
      interactionIdleCallback = window.requestIdleCallback(startLoading, {
        timeout: POSTHOG_INTERACTION_IDLE_TIMEOUT_MS,
      });

      return;
    }

    interactionLoadTimer = window.setTimeout(startLoading, 0);
  });
}

function handleFirstInteraction() {
  schedulePostHogAfterInteraction();
}

export function schedulePostHogInitialization() {
  if (
    typeof window === 'undefined' ||
    loadedClient ||
    loadingPromise ||
    initializationTimer !== null ||
    interactionListenersRegistered
  ) {
    return;
  }

  initializationTimer = window.setTimeout(() => {
    initializationTimer = null;
    void loadPostHog();
  }, POSTHOG_INITIALIZATION_DELAY_MS);

  window.addEventListener('pointerdown', handleFirstInteraction, {
    passive: true,
  });
  window.addEventListener('keydown', handleFirstInteraction);
  interactionListenersRegistered = true;
}

export function enqueuePostHogOperation(operation: PostHogOperation) {
  if (loadedClient) {
    operation(loadedClient);

    return;
  }

  if (pendingOperations.length >= MAX_PENDING_OPERATIONS) {
    void loadPostHog();

    return;
  }

  pendingOperations.push(operation);
  schedulePostHogInitialization();
}

export function getLoadedPostHog() {
  return loadedClient;
}
